// askSmart-ui/src/pages/TOSP/tabs/AuditTab.tsx
import React, { useEffect, useState } from "react";
import { useTospData } from "../TospDataContext";
import { budgetLabel, tenantLabel } from "../types";
import { listSales } from "../portal/tenantSalesApi";
import { TenantSalesMonth } from "../portal/tenantSales";
import { MONTH_NAMES } from "../portal/portalSummary";

/**
 * What the bulk sales importers stamp on a row they wrote.
 *
 * Matches the --by default in importTenantSalesYear.ts and
 * importTenantSalesFolder.ts. A row carrying it was loaded from the old
 * system, not reported by anyone, so it is left out of the log below.
 */
const IMPORT_LABEL = "import";

/** When · Tenancy · Action · Detail · IP · By. */
const COLS = "1.4fr 1.3fr 1.1fr 1.8fr 1.1fr 1.4fr";

interface AuditEntry {
  at: string;
  tenancy: string;
  action: string;
  detail: string;
  ip: string;
  by: string;
}

/**
 * What has been done to a tenancy, newest first.
 *
 * Derived rather than logged. Sales rows already record who reported a month
 * and when; transfers are recorded on the tenancy. Writing a second copy of
 * those facts would double the storage for every month of every tenant,
 * forever, and give the view something to disagree with the records about.
 *
 * The one thing that isn't already stored is the caller's IP, which the server
 * stamps on submission — it can't be taken from the browser, since an address
 * the client supplies is a claim rather than a fact.
 */
export default function AuditTab() {
  const { tenants, budgets } = useTospData();
  const [sales, setSales] = useState<TenantSalesMonth[]>([]);
  const [salesError, setSalesError] = useState<string | null>(null);

  useEffect(() => {
    listSales()
      .then(setSales)
      .catch(() =>
        setSalesError(
          "Couldn't load reported sales — transfers below are still complete.",
        ),
      );
  }, []);

  const nameFor = (tenantId: string) => {
    const found = tenants.find((tenant) => tenant.id === tenantId);
    return found ? tenantLabel(found) : tenantId;
  };

  const entries: AuditEntry[] = [];

  for (const tenant of tenants) {
    for (const move of tenant.transferLog || []) {
      entries.push({
        at: move.at,
        tenancy: tenantLabel(tenant),
        action: "Transferred",
        detail:
          `${move.fromEmail || "—"} → ${move.toName || move.toEmail}` +
          (move.documentsMoved > 0
            ? ` · ${move.documentsMoved} document${move.documentsMoved === 1 ? "" : "s"} moved`
            : move.serviceMoved
              ? " · documents left in place"
              : ""),
        ip: "",
        by: move.byName || move.byEmail || "—",
      });
    }
  }

  // Unlocking a published budget. Not a tenancy event, so the Tenancy column
  // names the budget instead — the alternative was sixty rows, one per
  // statement the unlock affected, which would bury every other entry.
  //
  // This one DOES carry an IP, because the unlock has its own endpoint and the
  // server stamps it there. Derived like everything else here: the record
  // lives on the budget as `unlockLog`, and this reads it.
  for (const budget of budgets) {
    for (const entry of budget.unlockLog || []) {
      entries.push({
        at: entry.at,
        tenancy: budgetLabel(budget),
        action: "Budget unlocked",
        detail: entry.reason,
        ip: entry.ip || "",
        by: entry.byName || entry.by || "—",
      });
    }
  }

  for (const row of sales) {
    const month = `${MONTH_NAMES[row.month - 1]} ${row.year}`;
    const amount = `$${Number(row.grossSales || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

    // History loaded from the old system in bulk isn't an event anybody did:
    // ninety months arriving at one timestamp under one label buries the
    // reporting the log exists to show. The figures are on the Sales screen
    // either way — this is the record of who filed what, and nobody filed
    // these. A correction to one of them still appears below, because that
    // WAS somebody.
    if (row.submittedBy !== IMPORT_LABEL) {
      entries.push({
        at: row.submittedAt,
        tenancy: nameFor(row.tenantId),
        action: "Sales reported",
        detail: `${month} · ${amount}`,
        ip: row.submittedIp || "",
        by: row.submittedBy || "—",
      });
    }

    // A correction is its own event. The figure it replaced is carried on the
    // row, so the entry can say what changed rather than only that something
    // did.
    if (row.amendedAt) {
      entries.push({
        at: row.amendedAt,
        tenancy: nameFor(row.tenantId),
        action: "Sales corrected",
        detail:
          `${month} · ` +
          (row.originalGrossSales !== undefined
            ? `$${Number(row.originalGrossSales).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} → ${amount}`
            : amount),
        ip: row.amendedIp || "",
        by: row.amendedBy || "—",
      });
    }
  }

  entries.sort((a, b) => (b.at || "").localeCompare(a.at || ""));

  return (
    <div className="tosp-panel tosp-panel-wide">
      <h2 className="tosp-panel-title">Audit Logs</h2>
      <p className="tosp-panel-note">
        Reported sales, corrections and tenancy transfers — who did what, when,
        and from where.
      </p>

      {salesError && (
        <div className="tosp-notice tosp-notice-error" role="alert">
          {salesError}
        </div>
      )}

      <div className="tosp-audit-table">
        <div className="tosp-audit-header" style={{ gridTemplateColumns: COLS }}>
          <div>When</div>
          <div>Tenancy</div>
          <div>Action</div>
          <div>Detail</div>
          <div>IP</div>
          <div>Action By</div>
        </div>

        {entries.length === 0 ? (
          <div className="tosp-audit-empty">Nothing recorded yet.</div>
        ) : (
          entries.map((entry, index) => (
            <div
              key={`${entry.at}-${index}`}
              className="tosp-audit-row"
              style={{ gridTemplateColumns: COLS }}
            >
              <div className="tosp-audit-when">
                {entry.at ? new Date(entry.at).toLocaleString() : "—"}
              </div>
              <div title={entry.tenancy}>{entry.tenancy}</div>
              <div>{entry.action}</div>
              <div title={entry.detail}>{entry.detail}</div>
              <div className="tosp-audit-when" title={entry.ip}>
                {entry.ip || "—"}
              </div>
              <div title={entry.by}>{entry.by}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
